import type { NotificationBus } from './NotificationBus';
import type { NotificationDto } from '../types';

/**
 * Polls GET /notifications/pending and emits a `notification.created` event on the bus
 * for each row returned.
 *
 * A hidden tab polls on a much longer interval: the popup it would render is invisible
 * anyway, and on production these background polls were half of the endpoint's traffic
 * (see docs/perf/POLLING_ENDPOINTS_OPTIMIZATION.md). Coming back to the tab polls once
 * immediately, so nothing is delayed for a user who is actually looking.
 *
 * Phase 1 is polling-only; WS transport is a parallel implementation (see WebSocketTransport.ts).
 */
export class PollingTransport {
  private timer: number | null = null;
  private readonly endpoint: string;
  private readonly intervalMs: number;
  private readonly hiddenIntervalMs: number;
  private readonly bus: NotificationBus;
  private inFlight = false;
  private lastPollAt = 0;

  constructor(opts: {
    bus: NotificationBus;
    endpoint?: string;
    intervalMs?: number;
    hiddenIntervalMs?: number;
  }) {
    this.bus        = opts.bus;
    this.endpoint   = opts.endpoint   ?? '/notifications/pending';
    this.intervalMs = opts.intervalMs ?? 60_000;
    // Default: a hidden tab polls five times more rarely than a visible one.
    this.hiddenIntervalMs = opts.hiddenIntervalMs ?? this.intervalMs * 5;
  }

  start(): void {
    if (this.timer !== null) return;
    void this.poll();
    this.schedule();
    window.addEventListener('focus', this.onWake);
    document.addEventListener('visibilitychange', this.onWake);
  }

  stop(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    window.removeEventListener('focus', this.onWake);
    document.removeEventListener('visibilitychange', this.onWake);
  }

  /**
   * Chained timeout rather than setInterval: the delay is re-read every tick, so a tab
   * that gets hidden slows down on its very next cycle instead of at the next start().
   */
  private schedule(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      void this.poll();
      this.schedule();
    }, this.currentIntervalMs());
  }

  private currentIntervalMs(): number {
    return document.hidden ? this.hiddenIntervalMs : this.intervalMs;
  }

  /**
   * Focus and visibilitychange both fire on a single alt-tab back into the page, and
   * focus alone fires on every click back into the window. Polling on each of them was
   * a large share of the endpoint's traffic, so a wake-up poll only happens when the
   * regular interval has already elapsed; otherwise the pending timer is enough.
   */
  private onWake = (): void => {
    if (document.hidden) {
      this.schedule();
      return;
    }
    if (Date.now() - this.lastPollAt >= this.intervalMs) {
      void this.poll();
    }
    this.schedule();
  };

  private async poll(): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    this.lastPollAt = Date.now();
    try {
      const res = await fetch(this.endpoint, { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = (await res.json()) as { notifications?: NotificationDto[] };
      const items = data.notifications ?? [];
      for (const item of items) {
        this.bus.emit('notifications', { type: 'notification.created', payload: item });
      }
    } catch (e) {
      console.warn('[PollingTransport] poll failed:', e);
    } finally {
      this.inFlight = false;
    }
  }
}
